import {
  AfterContentChecked,
  AfterContentInit,
  ContentChild,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormControlDirective,
  FormControlName,
  NgModel,
} from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/observable/merge';

function hasSyncRequiredValidator(control: AbstractControl | null): boolean {
  if (!control || typeof control.validator !== 'function') {
    return false;
  }
  try {
    const formControl = new FormControl(null, control.validator);
    formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    return formControl.hasError('required');
  } catch (e) {
    return false;
  }
}

@Directive({
  selector:
    'mat-input-container:not(.no-required-marker),mat-form-field:not(.no-required-marker)',
})
export class RequiredFieldMarkerDirective
  implements AfterContentInit, AfterContentChecked, OnDestroy
{
  @ContentChild(FormControlDirective)
  private fcDir: FormControlDirective;
  @ContentChild(FormControlName)
  private fcName: FormControlName;
  @ContentChild(NgModel)
  private ngModel: NgModel;

  private sub: Subscription | null = null;
  private starEl: HTMLElement | null = null;
  private boundControl: AbstractControl | null = null;
  private lastShown: boolean | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngAfterContentInit(): void {
    this.refreshBinding();
  }

  ngAfterContentChecked(): void {
    this.refreshBinding();
  }

  ngOnDestroy(): void {
    this.clearSubscription();
    this.removeStarDom();
  }

  private getControl(): AbstractControl | null {
    if (this.fcDir && this.fcDir.control) {
      return this.fcDir.control;
    }
    if (this.fcName && this.fcName.control) {
      return this.fcName.control;
    }
    if (this.ngModel && this.ngModel.control) {
      return this.ngModel.control;
    }
    return null;
  }

  private refreshBinding(): void {
    const control = this.getControl();
    if (control !== this.boundControl) {
      this.clearSubscription();
      this.boundControl = control;
      if (!control) {
        this.removeStarDom();
        return;
      }
      this.sub = Observable.merge(
        control.statusChanges,
        control.valueChanges
      ).subscribe(() => this.applyVisual(control));
    }
    if (this.boundControl) {
      this.applyVisual(this.boundControl);
    }
  }

  private clearSubscription(): void {
    if (this.sub) {
      this.sub.unsubscribe();
      this.sub = null;
    }
  }

  /**
   * Cible pour l’astérisque : mat-label, sinon libellé flottant Material (placeholder).
   */
  private findLabelOrPlaceholderNode(host: HTMLElement): HTMLElement | null {
    const matLabel = host.querySelector('mat-label') as HTMLElement | null;
    if (matLabel) {
      return matLabel;
    }
    const labelPh = host.querySelector('label.mat-input-placeholder') as HTMLElement | null;
    if (labelPh) {
      return labelPh;
    }
    const ph = host.querySelector('.mat-input-placeholder') as HTMLElement | null;
    if (ph) {
      return ph;
    }
    return null;
  }

  private shouldShowRequiredMarker(
    control: AbstractControl,
    host: HTMLElement
  ): boolean {
    if (!hasSyncRequiredValidator(control)) {
      return false;
    }
    
    if (!this.findLabelOrPlaceholderNode(host)) {
      return false;
    }
    return true;
  }

  private applyVisual(control: AbstractControl): void {
    const host = this.el.nativeElement as HTMLElement;
    const show = this.shouldShowRequiredMarker(control, host);
    if (show === this.lastShown) {
      if (show && this.starEl && !this.starEl.parentNode) {
        this.attachStarNextToTitle(host);
      }
      return;
    }
    this.lastShown = show;
    if (show) {
      this.ensureStar(host);
    } else {
      this.removeStarDom();
    }
  }

  private ensureStar(host: HTMLElement): void {
    this.renderer.addClass(host, 'mat-has-required-marker');
    if (this.starEl) {
      return;
    }
    this.starEl = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(this.starEl, 'mat-required-asterisk');
    this.renderer.setAttribute(this.starEl, 'aria-hidden', 'true');
    this.renderer.appendChild(this.starEl, this.renderer.createText('*'));
    this.attachStarNextToTitle(host);
  }

  /** Astérisque après mat-label ou après le texte du placeholder flottant. */
  private attachStarNextToTitle(host: HTMLElement): void {
    const place = () => {
      if (!this.starEl || this.starEl.parentNode) {
        return;
      }
      const target = this.findLabelOrPlaceholderNode(host);
      if (target) {
        this.renderer.appendChild(target, this.starEl);
      }
    };
    place();
    if (this.starEl && !this.starEl.parentNode) {
      setTimeout(place, 0);
    }
  }

  private removeStarDom(): void {
    this.lastShown = false;
    const host = this.el.nativeElement as HTMLElement;
    if (host) {
      this.renderer.removeClass(host, 'mat-has-required-marker');
    }
    if (this.starEl) {
      const parent = this.starEl.parentNode as HTMLElement | null;
      if (parent) {
        this.renderer.removeChild(parent, this.starEl);
      }
      this.starEl = null;
    }
  }
}



// WEBPACK FOOTER //
// C:/Users/DENISE/Documents/IT4U/web-callcenter/src/app/shared/directives/required-field-marker/required-field-marker.directive.ts